<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useAuth } from '../../composables/useAuth';
import { usePosStore } from '../../stores/posStore';
import PosCatalogPanel from './PosCatalogPanel.vue';
import PosSpatialEditor from './PosSpatialEditor.vue';
import PosResultsPanel from './PosResultsPanel.vue';

const props = defineProps<{ shareToken?: string | null }>();
const store = usePosStore();
const { isLoggedIn, login } = useAuth();
const fileInput = ref<HTMLInputElement | null>(null);
const shareLink = ref<string | null>(null);
const notice = ref<string | null>(null);
const busy = ref(false);
const canEdit = computed(() => !store.readOnly);

function download(filename: string, text: string, mime = 'application/json'): void {
  const blob = new Blob([text], { type: `${mime};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

async function save(): Promise<void> {
  if (!isLoggedIn.value) {
    notice.value = 'Log in with EVE before saving a server plan.';
    await login();
    return;
  }
  busy.value = true;
  notice.value = null;
  try { await store.save(); notice.value = 'Plan saved.'; }
  catch (error) { notice.value = error instanceof Error ? error.message : 'Unable to save POS plan.'; }
  finally { busy.value = false; }
}

async function duplicate(): Promise<void> {
  busy.value = true;
  try { await store.duplicate(); notice.value = 'Editable duplicate created.'; }
  catch (error) { notice.value = error instanceof Error ? error.message : 'Unable to duplicate plan.'; }
  finally { busy.value = false; }
}

async function share(): Promise<void> {
  busy.value = true;
  try { shareLink.value = await store.share(); notice.value = 'Read-only share link created.'; }
  catch (error) { notice.value = error instanceof Error ? error.message : 'Unable to create share link.'; }
  finally { busy.value = false; }
}

async function copyShare(): Promise<void> {
  if (shareLink.value || store.shareUrl) await navigator.clipboard.writeText(shareLink.value || store.shareUrl!);
}

async function revokeShare(): Promise<void> {
  busy.value = true;
  try { await store.revokeShare(); shareLink.value = null; notice.value = 'Share link revoked.'; }
  catch (error) { notice.value = error instanceof Error ? error.message : 'Unable to revoke share link.'; }
  finally { busy.value = false; }
}

async function deletePlan(): Promise<void> {
  if (!window.confirm('Delete this saved POS plan? This cannot be undone.')) return;
  busy.value = true;
  try { await store.deleteCurrent(); notice.value = 'Plan deleted.'; }
  catch (error) { notice.value = error instanceof Error ? error.message : 'Unable to delete POS plan.'; }
  finally { busy.value = false; }
}

function exportJson(): void { download('evefit-pos-plan.json', store.exportPlan()); }

async function importFile(event: Event): Promise<void> {
  const file = (event.target as HTMLInputElement).files?.[0];
  if (!file) return;
  try {
    const text = await file.text();
    const document = JSON.parse(text) as unknown;
    if (isLoggedIn.value) await store.importServerDocument(document);
    else store.importPlan(text);
    notice.value = 'POS plan imported.';
  } catch (error) {
    notice.value = error instanceof Error ? error.message : 'Unable to import POS plan.';
  } finally {
    if (fileInput.value) fileInput.value.value = '';
  }
}

function newDraft(): void { store.beginLocalDraft(true); notice.value = 'Started a new local draft.'; }

onMounted(async () => {
  try {
    if (props.shareToken) await store.loadShare(props.shareToken);
    else store.beginLocalDraft();
    await Promise.all([store.loadCatalog(), isLoggedIn.value ? store.loadPlans() : Promise.resolve()]);
  } catch (error) {
    notice.value = error instanceof Error ? error.message : 'Unable to load POS Builder.';
  }
});
</script>

<template>
  <main class="pos-builder">
    <section class="pos-toolbar" aria-label="POS plan controls">
      <div class="plan-fields">
        <label>Plan name <input v-model="store.plan.name" :disabled="store.readOnly" maxlength="120"></label>
        <label>Environment
          <select v-model="store.plan.environment" :disabled="store.readOnly">
            <option value="highsec">High security</option><option value="lowsec">Low security</option>
            <option value="nullsec">Null security</option><option value="wormhole">Wormhole</option>
          </select>
        </label>
        <label>Security <input v-model.number="store.plan.securityStatus" :disabled="store.readOnly" min="-1" max="1" step="0.1" type="number"></label>
        <label v-if="isLoggedIn && store.ownedPlans.length">Saved plans
          <select :value="store.plan.id ?? ''" :disabled="busy" @change="store.loadOwned(Number(($event.target as HTMLSelectElement).value))">
            <option value="">Open a saved plan</option>
            <option v-for="saved in store.ownedPlans" :key="saved.id" :value="saved.id">{{ saved.name }}</option>
          </select>
        </label>
      </div>
      <label class="notes-field">Planning notes <textarea v-model="store.plan.notes" :disabled="store.readOnly" maxlength="10000" rows="2"></textarea></label>
      <div class="toolbar-actions">
        <button @click="newDraft">New local draft</button><button :disabled="busy" @click="fileInput?.click()">Import</button>
        <button @click="exportJson">Export JSON</button><button v-if="canEdit" :disabled="busy" @click="save">{{ store.isSaved ? 'Save' : 'Save to account' }}</button>
        <button :disabled="busy" @click="duplicate">Duplicate</button><button v-if="canEdit && store.isSaved" :disabled="busy" @click="share">Share</button>
        <button v-if="canEdit && store.shareUrl" :disabled="busy" @click="revokeShare">Revoke share</button>
        <button v-if="canEdit && store.isSaved" class="danger" :disabled="busy" @click="deletePlan">Delete saved plan</button>
      </div>
      <input ref="fileInput" class="hidden" type="file" accept="application/json,.json" @change="importFile">
      <p v-if="store.readOnly" class="readonly">This is a read-only shared plan. Duplicate it to edit.</p>
      <p v-if="notice || store.error" class="notice" aria-live="polite">{{ notice || store.error }}</p>
      <div v-if="shareLink || store.shareUrl" class="share-link"><input readonly :value="shareLink || store.shareUrl || ''" aria-label="Read-only POS share link" @focus="($event.target as HTMLInputElement).select()"><button @click="copyShare">Copy link</button></div>
    </section>
    <section class="pos-grid"><PosCatalogPanel /><PosSpatialEditor /><PosResultsPanel /></section>
  </main>
</template>

<style scoped>
.pos-builder{display:flex;flex-direction:column;flex:1;min-height:0;gap:10px;padding:10px;overflow:hidden}.pos-toolbar{border:1px solid var(--border-color);border-radius:var(--radius-lg);padding:9px;background:linear-gradient(90deg,rgba(0,212,255,.06),transparent 45%),var(--bg-panel)}.plan-fields,.toolbar-actions{display:flex;flex-wrap:wrap;gap:7px;align-items:end}.plan-fields label,.notes-field{display:flex;flex-direction:column;gap:3px;color:var(--text-secondary);font-size:.66rem;font-family:var(--font-mono);letter-spacing:.04em;text-transform:uppercase}.plan-fields input,.plan-fields select,.share-link input,.notes-field textarea{background:var(--bg-secondary);border:1px solid var(--border-color);border-radius:var(--radius-sm);color:var(--text-primary);padding:6px 7px}.notes-field{margin-top:7px}.notes-field textarea{resize:vertical;min-height:38px}.toolbar-actions{margin-top:8px}.toolbar-actions button,.share-link button{background:var(--bg-raised);border:1px solid var(--border-color);border-radius:var(--radius-sm);color:var(--text-primary);padding:6px 9px;font-size:.7rem;cursor:pointer}.toolbar-actions .danger{border-color:var(--accent-red);color:#ffaaa5}.toolbar-actions button:disabled{opacity:.5}.hidden{display:none}.readonly,.notice{margin:7px 0 0;font-size:.74rem}.readonly{color:#e4ad4e}.notice{color:var(--text-secondary)}.share-link{display:flex;gap:6px;margin-top:7px}.share-link input{flex:1}.pos-grid{display:grid;grid-template-columns:minmax(280px,340px) minmax(420px,1fr) minmax(310px,360px);gap:10px;flex:1;min-height:0}.pos-grid>*{box-shadow:var(--glow-panel)}@media(max-width:1150px){.pos-grid{grid-template-columns:minmax(240px,300px) minmax(400px,1fr)}.pos-grid>:last-child{grid-column:1/-1;min-height:360px}}@media(max-width:900px){.pos-builder{overflow:visible}.pos-grid{display:flex;flex-direction:column}.pos-grid>*{min-height:420px}.plan-fields label:first-child{width:100%}.share-link{flex-wrap:wrap}.share-link input{min-width:100%}}
</style>
